import type { NotificationCategory, NotificationPrefs } from "./types";

export type NotificationCapabilityId =
  | "messaging.conversations.view"
  | "support.tickets.view"
  | "crm.tasks.view"
  | "crm.contacts.view"
  | "meetings.manage"
  | "proposals.view";

/** Categories without a capability (system) are shown to whoever got them. */
export const NOTIFICATION_CATEGORY_CAPABILITY: Partial<
  Record<NotificationCategory, NotificationCapabilityId>
> = {
  messages_sms: "messaging.conversations.view",
  messages_internal: "messaging.conversations.view",
  tickets_message: "support.tickets.view",
  tickets_assigned: "support.tickets.view",
  tasks_mention: "crm.tasks.view",
  tasks_rescheduled: "crm.tasks.view",
  threads_mention: "crm.contacts.view",
  leads_followup_due: "crm.contacts.view",
  leads_stale: "crm.contacts.view",
  bookings_new: "meetings.manage",
  invoices_payment_failed: "proposals.view",
};

export const NOTIFICATION_CATEGORY_GROUPS: {
  title: string;
  keys: Exclude<NotificationCategory, "system">[];
}[] = [
  {
    title: "Messages",
    keys: ["messages_sms", "messages_internal"],
  },
  {
    title: "Tickets",
    keys: ["tickets_message", "tickets_assigned"],
  },
  {
    title: "Tasks",
    keys: ["tasks_mention", "tasks_rescheduled"],
  },
  {
    title: "Threads",
    keys: ["threads_mention"],
  },
  {
    title: "Leads",
    keys: ["leads_followup_due", "leads_stale"],
  },
  {
    title: "Bookings",
    keys: ["bookings_new"],
  },
  {
    title: "Billing",
    keys: ["invoices_payment_failed"],
  },
];

/** Categories members can switch off (system news is always on). */
export const prefsKeyForCategory = (
  category: Exclude<NotificationCategory, "system">,
): keyof NotificationPrefs => category;

export const canAccessNotificationCategory = (
  category: NotificationCategory,
  hasCapability: (id: string) => boolean,
): boolean => {
  const capability = NOTIFICATION_CATEGORY_CAPABILITY[category];
  return capability ? hasCapability(capability) : true;
};

export const filterNotificationCategoryGroups = (
  hasCapability: (id: string) => boolean,
) =>
  NOTIFICATION_CATEGORY_GROUPS.map((group) => ({
    ...group,
    keys: group.keys.filter((key) =>
      canAccessNotificationCategory(key, hasCapability),
    ),
  })).filter((group) => group.keys.length > 0);

export const sanitizeNotificationPrefsForCapabilities = (
  prefs: NotificationPrefs,
  hasCapability: (id: string) => boolean,
): NotificationPrefs => {
  const next = { ...prefs };
  for (const category of Object.keys(
    NOTIFICATION_CATEGORY_CAPABILITY,
  ) as Exclude<NotificationCategory, "system">[]) {
    if (!canAccessNotificationCategory(category, hasCapability)) {
      next[prefsKeyForCategory(category)] = false;
    }
  }
  return next;
};
